import {inject, Injectable} from '@angular/core';
import {BloggerGroupsApiService} from "./blogger-groups-api.service";
import {AttachDetachBloggersByGroup, BloggerGroupsData, CreateBloggerGroupParams} from "../types/blogger-groups.types";
import {defaultRecordsDataRequestQueryParams} from "../../../../models/default-records-data-request-query-params";
import {bloggerGroupsResourceType} from "../configs/blogger-groups-config";
import {defaultPageableRecordData} from "../../../../models/default-pageable-record-data";
import {BloggerGroupsStateService} from "./blogger-groups-state.service";
import {FiltersValues, RecordData, RecordsDataRequestQueryParams} from "../../../../types/records-table.types";
import {Pageable} from "../../../../types/pagable.types";
import {RecordTableApiService} from "../../../../services-api/record-table-api.service";

@Injectable({
  providedIn: 'root'
})
export class BloggerGroupsService {
  private bloggerGroupsApiService: BloggerGroupsApiService = inject(BloggerGroupsApiService);
  private bloggerGroupsStateService: BloggerGroupsStateService = inject(BloggerGroupsStateService);
  private recordTableApiService: RecordTableApiService = inject(RecordTableApiService);

  public async getBloggerGroupsData(bloggerId?: number): Promise<void> {
    const recordsDataRequestQueryParams = this.getRecordsDataRequestQueryParams(bloggerId);
    const bloggerGroupsData = await this.getRecordData(bloggerGroupsResourceType.path, recordsDataRequestQueryParams).catch(() => defaultPageableRecordData);
    const bloggerGroups = bloggerGroupsData.content
      ?.filter((item): item is BloggerGroupsData => 'id' in item)
      ?.sort((a, b) => a.id - b.id) || [];

    this.bloggerGroupsStateService.setBloggerGroups(bloggerGroups)
  }

  public createBloggerGroup(createBloggerGroupParams: CreateBloggerGroupParams): Promise<BloggerGroupsData> {
    return new Promise((resolve, reject) => {
      this.bloggerGroupsApiService.createBloggerGroup(createBloggerGroupParams).subscribe({
        next: (data) => resolve(data),
        error: (error) => reject(error)
      });
    });
  }

  public attachBloggersByGroup(attachDetachBloggersByGroup: AttachDetachBloggersByGroup): Promise<void> {
    return new Promise((resolve, reject) => {
      this.bloggerGroupsApiService.attachBloggersByGroup(attachDetachBloggersByGroup).subscribe({
        next: () => resolve(),
        error: (error) => reject(error)
      });
    });
  }

  public detachBloggersByGroup(attachDetachBloggersByGroup: AttachDetachBloggersByGroup): Promise<void> {
    return new Promise((resolve, reject) => {
      this.bloggerGroupsApiService.detachBloggersByGroup(attachDetachBloggersByGroup).subscribe({
        next: () => resolve(),
        error: (error) => reject(error)
      });
    });
  }

  public deleteBloggerGroup(id: number): Promise<void> {
    return new Promise((resolve, reject) => {
      this.bloggerGroupsApiService.deleteBloggerGroup(id).subscribe({
        next: () => resolve(),
        error: (error) => reject(error)
      });
    });
  }

  private getRecordData(path: string, queryParams: RecordsDataRequestQueryParams): Promise<Pageable<RecordData>> {
    return new Promise((resolve, reject) => {
      this.recordTableApiService.fetchRecordTableData(path, queryParams).subscribe({
        next: (recordsTableData: Pageable<RecordData>) => resolve(recordsTableData),
        error: (error) => reject(error)
      })
    });
  }

  private getRecordsDataRequestQueryParams(bloggerId?: number): RecordsDataRequestQueryParams {
    const recordsDataRequestQueryParams = {
      ...defaultRecordsDataRequestQueryParams,
      size: undefined
    };

    if (!bloggerId) { return recordsDataRequestQueryParams; }
    const filtersValues: FiltersValues = { bloggerId: bloggerId }

    return {
      ...recordsDataRequestQueryParams,
      ...filtersValues
    }
  }
}
